import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Typography,
} from '@mui/material';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import styled from '@emotion/styled';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import {
  setContractReference,
  setRequiresSignature,
  setStripeConfigType,
  setSubscriptionId,
} from '../santeMobiliteSlice';
import { subscriptionSanteMobiliteApi } from '@/api/santeMobiliteApi';
import { appError, appLoading, appSuccess } from '@/redux/actions/appActions';
import { trackDatadogAction, trackDatadogError } from '@/services/datadog';
import { Country } from '../types';
import ErrorBoundary from '../../../components/ErrorBoundary/ErrorBoundary';

const ButtonStyled = styled(Button)`
  background-color: #2f6975;
  color: white;
  border-radius: 30px;
  width: 100%;
  height: 40px;
  font-weight: 600;
  font-size: 15px;
  margin-top: 16px;
  &:hover {
    background-color: #26555f;
  }
`;

interface Props {
  startDate: string;
  endDate: string;
  country: Country;
}

// Exporté pour être testé isolément : le composant parent ne le rend qu'après
// un appel réseau et le montage de <Elements>, ce qui rendrait le test du
// bouton et du rattrapage d'erreur inutilement indirect.
export const CheckoutForm: React.FC = () => {
  const stripe = useStripe();
  const elements = useElements();
  const { t } = useTranslation('common');
  const [loading, setLoading] = useState(false);
  // `elements` existe dès que <Elements> est monté ; il ne dit rien du montage
  // effectif de l'iframe. Sans cette garde, un clic sur Payer trop tôt part en
  // IntegrationError « elements should have a mounted Payment Element ».
  const [ready, setReady] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setLoading(true);
    setErrorMessage('');

    trackDatadogAction('sante_mobilite_stripe_confirm_payment');

    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/insurance/health-mobility/success-payment`,
        },
      });

      if (result.error) {
        setErrorMessage(result.error.message || t('payment.start-failed'));
        trackDatadogError(
          new Error(result.error.message || 'Stripe confirmPayment failed'),
          {
            action: 'sante_mobilite_stripe_confirm_payment_failed',
            stripeErrorType: result.error.type,
            stripeErrorCode: result.error.code,
          },
        );
      }
    } catch (err) {
      // confirmPayment LÈVE ses IntegrationError au lieu de les retourner dans
      // result.error. C'est exactement ce qui s'est produit le 24/09 : la
      // promesse rejette, setLoading(false) n'est jamais atteint, le bouton
      // reste bloqué en spinner et rien ne remonte dans Datadog.
      setErrorMessage(t('payment.start-failed'));
      trackDatadogError(err as Error, {
        action: 'sante_mobilite_stripe_confirm_payment_threw',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {/* link: 'never' désactive le wallet Stripe Link (friction email/tel inutile,
          l'email est déjà attaché au customer côté back). Cast nécessaire car les
          types de @stripe/stripe-js@1.17 ne déclarent pas encore `link` dans
          PaymentWalletsOption, mais le runtime Stripe.js (CDN, à jour) le supporte. */}
      <PaymentElement
        options={{ wallets: { link: 'never' } as never }}
        onReady={() => setReady(true)}
      />
      {errorMessage && (
        <Typography sx={{ mt: 1, color: 'error.main', fontSize: 13 }}>
          {errorMessage}
        </Typography>
      )}
      <ButtonStyled type="submit" disabled={!stripe || !ready || loading}>
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Payer'}
      </ButtonStyled>
    </form>
  );
};

const SanteMobilitePaymentForm: React.FC<Props> = ({
  startDate,
  endDate,
  country,
}) => {
  const dispatch = useAppDispatch();
  const contractReference = useAppSelector(
    state => state.santeMobilite.contractReference,
  );
  const hasFrenchSocialSecurity = useAppSelector(
    state => state.santeMobilite.hasFrenchSocialSecurity,
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [stripePromise, setStripePromise] = useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    const createSession = async () => {
      try {
        dispatch(appLoading());
        const response = await subscriptionSanteMobiliteApi({
          startDate,
          endDate,
          country,
          contractReference,
          hasFrenchSocialSecurity,
        });
        const {
          clientSecret: secret,
          publicKey,
          contractReference: ref,
          stripeConfigType,
          subscriptionId,
          requiresSignature,
        } = response.data;

        setClientSecret(secret);
        // publicKey différente de RC/Habitation : Santé Mobilité utilise un compte
        // Stripe distinct, on charge donc Stripe.js avec sa propre clé publique.
        setStripePromise(loadStripe(publicKey));
        dispatch(setContractReference(ref));
        dispatch(setStripeConfigType(stripeConfigType));
        if (subscriptionId !== undefined) {
          dispatch(setSubscriptionId(subscriptionId));
        }
        // requiresSignature : true pour zones 1/2/3 (SellAndSign post-paiement),
        // false pour zone 4. La SuccessPage l'utilise pour décider de rediriger
        // vers SellAndSign ou d'afficher directement le merci.
        if (typeof requiresSignature === 'boolean') {
          dispatch(setRequiresSignature(requiresSignature));
        }

        // Persist contractReference + requiresSignature pour le redirect Stripe
        // (récupération sur /success au cas où Redux serait reset entre temps).
        localStorage.setItem('sante_mobilite_contractReference', ref);
        if (typeof requiresSignature === 'boolean') {
          localStorage.setItem(
            'sante_mobilite_requiresSignature',
            requiresSignature ? '1' : '0',
          );
        }

        // Event de diagnostic clé : on capture ce que le back a réellement
        // renvoyé (notamment requiresSignature) pour pouvoir tracer la suite
        // du parcours côté Datadog. C'est l'event qui aurait permis de
        // diagnostiquer immédiatement le bug "SuccessPaymentPage skippée".
        trackDatadogAction('sante_mobilite_session_success', {
          contractReference: ref,
          requiresSignature:
            typeof requiresSignature === 'boolean'
              ? requiresSignature
              : 'absent',
          stripeConfigType,
          hasSubscriptionId: subscriptionId !== undefined,
        });

        dispatch(appSuccess());
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error creating Santé Mobilité session:', err);
        trackDatadogError(err as Error, {
          action: 'sante_mobilite_session_failed',
          startDate,
          endDate,
          countryId: country?.id,
          countryName: country?.name,
        });
        dispatch(appError());
      }
    };

    createSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startDate, endDate, dispatch]);

  if (!clientSecret || !stripePromise) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom textAlign="center">
        Paiement sécurisé
      </Typography>
      <ErrorBoundary variant="payment">
        <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
          <CheckoutForm />
        </Elements>
      </ErrorBoundary>
    </Box>
  );
};

export default SanteMobilitePaymentForm;
